import { useCallback } from 'react';
import { updateTaskProgress } from '@apis/schedule';
import type { SiteId, TaskId } from '@interfaces/IScheduler';
import { useImmediateWritePersist } from './useImmediateWritePersist';

interface IProgressWrite {
  progressPercent: number;
}

/**
 * Immediate-write persistence for a COMMITTED task's progress percent (the
 * Overview slider). Progress travels alone since the task-commit design: the
 * task's status is a plan fact with its own route, and the activity's own
 * status is Trusstor's. Returns `persist(progressPercent)`, which queues the
 * value on the
 * task's SHARED write channel (`immediateWriteChannel`): one debounce / one
 * in-flight PUT per task no matter how many surfaces are mounted, values
 * coalesce to the latest, and
 * requests are serialized so out-of-order responses can't leave the server
 * behind the UI. Phase 2: the request LOSES the `activity` array — the log is
 * server-generated (invariant 9), and the store mirrors the structured entry
 * optimistically. A pending write flushes if the surface unmounts or the
 * task/schedule changes mid-debounce.
 *
 * Failure handling: the channel retries what may yet succeed — the whole-doc
 * Save deliberately does NOT write these fields for existing tasks (see
 * backend `SchedulesRepoPort.save`), so the retry IS the recovery path. A value
 * given up is rolled back to the server's and a toast says so
 * (`useImmediateWritePersist`). No-op when `scheduleNumber` is absent (e.g. the Router-free
 * component unit test) — the store still updates.
 */
export function useTaskProgressPersist(
  siteId: SiteId,
  scheduleNumber: string | undefined,
  taskId: TaskId,
): (progressPercent: number) => void {
  const { write } = useImmediateWritePersist(siteId, scheduleNumber, taskId);

  return useCallback(
    (progressPercent: number) => {
      write<IProgressWrite>(['progress'], { progressPercent }, (v, ctx) =>
        updateTaskProgress(ctx.siteId, ctx.scheduleNumber, ctx.taskId, v.progressPercent),
      );
    },
    [write],
  );
}
